import React, {useEffect, useId, useRef} from 'react';
import {createPortal} from 'react-dom';
import {createRoot} from 'react-dom/client';
import {FiAlertTriangle, FiX} from 'react-icons/fi';
import './action-dialog.css';

/** One accessible dialog shell for confirmations and exam workspaces. */
export default function ActionDialog({open, title, subtitle, children, onClose, busy=false, danger=false, size='md', footer, className='', closeLabel}) {
 const ref=useRef(null), titleId=useId(), closeRef=useRef(onClose);
 closeRef.current=onClose;
 useEffect(()=>{
  const node=ref.current;
  if(open&&!node.open)node.showModal();
  if(!open&&node.open)node.close();
  return()=>{if(node.open)node.close();};
 },[open]);
 if(typeof document==='undefined')return null;
 return createPortal(<dialog ref={ref} aria-labelledby={titleId} className={`sg-dialog sg-dialog-${size} ${className}`} onCancel={e=>{if(e.target!==ref.current)return;e.preventDefault();if(!busy)closeRef.current?.();}}>
  {open&&<><div className="h-1.5 bg-gradient-to-r from-[#7f083c] to-[#c90057]"/>
   <header className="sg-dialog-header flex items-start gap-4 border-b border-slate-100 px-5 py-5 sm:px-7">
    {danger&&<span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-rose-50 text-[#b41455]"><FiAlertTriangle size={23}/></span>}
    <div className="min-w-0 flex-1"><h2 id={titleId} className="m-0 text-xl font-bold text-slate-900">{title}</h2>{subtitle&&<p className="mb-0 mt-1 text-sm leading-6 text-slate-500">{subtitle}</p>}</div>
    <button type="button" className="sg-icon-button" aria-label={closeLabel||`Close ${title}`} disabled={busy} onClick={onClose}><FiX size={20}/></button>
   </header>
   <div className="sg-dialog-body px-5 py-5 sm:px-7">{children}</div>
   {footer&&<footer className="sg-dialog-footer flex flex-wrap justify-end gap-3 border-t border-slate-100 bg-slate-50 px-5 py-4 sm:px-7">{footer}</footer>}
  </>}
 </dialog>,document.body);
}

let confirmationQueue=Promise.resolve();
/** Async, explicit confirmation; never replaces the browser's global confirm. */
export function confirmAction({title='Confirm action',message,confirmLabel='Confirm',cancelLabel='Cancel',danger=true}={}) {
 const show=()=>new Promise(resolve=>{
  const host=document.createElement('div');document.body.appendChild(host);
  const root=createRoot(host);let complete=false;
  const finish=value=>{if(complete)return;complete=true;root.unmount();host.remove();resolve(value);};
  root.render(<ActionDialog open title={title} danger={danger} size="sm" onClose={()=>finish(false)} footer={<><button type="button" autoFocus className="sg-button" onClick={()=>finish(false)}>{cancelLabel}</button><button type="button" className="sg-button sg-primary" onClick={()=>finish(true)}>{confirmLabel}</button></>}><p className="m-0 whitespace-pre-wrap text-sm leading-7 text-slate-600">{message}</p></ActionDialog>);
 });
 const result=confirmationQueue.then(show,show);confirmationQueue=result.catch(()=>false);return result;
}
